// See useAuditEventStream.spec.md for AES-FR-001 through AES-FR-004 (shared by NCP-FR-002,
// NCP-FR-008, NCP-FR-009 and NB-FR-003)

import { useCallback, useEffect, useRef, useState } from 'react'
import ApiClient from 'api-client/ApiClient'
import type { TimeRange } from 'core/components/widgets/timeRanges'
import useIsMounted from 'core/hooks/useIsMounted'
import {
  matchesEventFilters,
  mergeNewEvents,
  newestLoadedTimestamp,
  parseTimestamp,
  sortNewestFirst,
} from './helpers'
import type { AuditEvent, AuditEventFilters } from './models'
import { getTimeWindow } from './timeframes'

const { resMgr } = ApiClient.getInstance()

// AES-FR-003: A background refresh starts this far before the newest loaded event, so a record the
// audit service ingests late (with an earlier timestamp than events already shown) is still
// picked up; the overlap's repeats are dropped by ID when merging
export const REFRESH_OVERLAP_MS = 2 * 60 * 1000

// The refresh's start: the overlap before `since`, but never before the window itself
const refreshStart = (since: string, windowStart: string): string => {
  // `since` comes from newestLoadedTimestamp, so it always parses
  const start = (parseTimestamp(since) ?? 0) - REFRESH_OVERLAP_MS
  return new Date(Math.max(start, parseTimestamp(windowStart) ?? start)).toISOString()
}

// AES-FR-005: After this long without an answer a request counts as abandoned, so polling can
// try again (a late answer to it is still ignored by the latest-request guard)
export const STALE_REQUEST_MS = 60 * 1000

// Same events in the same order (the merge keeps the loaded objects), so nothing changed
const sameEvents = (a: AuditEvent[], b: AuditEvent[]): boolean =>
  a.length === b.length && a.every((event, i) => event === b[i])

// A stable "no filters" value, so callers without filters don't trigger a refetch every render
export const NO_FILTERS: AuditEventFilters = Object.freeze({})

interface Options {
  timeframe: TimeRange
  // Must keep the same identity until the filters change (a new object refetches)
  filters?: AuditEventFilters
  // Most events requested and kept; left out for no limit
  limit?: number
}

interface LoadedQuery {
  timeframe: TimeRange
  filters: AuditEventFilters
  events: AuditEvent[]
}

// Audit events for a timeframe, newest first, shared by the Notification Center page and the
// notification bar
export default function useAuditEventStream({ timeframe, filters = NO_FILTERS, limit }: Options) {
  const [events, setEvents] = useState<AuditEvent[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const isMounted = useIsMounted()
  // AES-FR-002: Only the most recent request may update state
  const latestRequestId = useRef(0)
  // The events on screen and the query they were loaded for
  const loaded = useRef<LoadedQuery | null>(null)
  // AES-FR-005: The request still waiting for an answer, if any, and when it started
  const pending = useRef<{ id: number; startedAt: number } | null>(null)

  // AES-FR-001: Fetch the timeframe with the filters, newest first.
  // AES-FR-003: A background refresh keeps the rows on screen and only asks for events from the
  // newest loaded one on, merged into what's loaded (a full fetch if the query changed).
  const fetchEvents = useCallback(
    async (inBackground: boolean) => {
      // AES-FR-005: A poll never stacks on a request that is still waiting (unless it is
      // abandoned); a foreground load (mount, query change, Retry) always goes out
      if (inBackground && pending.current) {
        if (Date.now() - pending.current.startedAt < STALE_REQUEST_MS) return
      }
      const requestId = ++latestRequestId.current
      pending.current = { id: requestId, startedAt: Date.now() }
      const isLatest = () => isMounted() && requestId === latestRequestId.current
      const current = loaded.current
      const sameQuery = current?.timeframe === timeframe && current.filters === filters
      // AES-FR-004: Rows from another timeframe or filters are never shown as this query's
      if (!sameQuery && current) {
        loaded.current = null
        setEvents([])
      }
      if (!inBackground) setLoading(true)
      setError(false)
      const range = getTimeWindow(timeframe)
      const previous = inBackground && sameQuery ? current.events : null
      const since = previous && newestLoadedTimestamp(previous)
      try {
        const response = await resMgr.getAuditEvents({
          ...range,
          ...(since ? { startTime: refreshStart(since, range.startTime) } : {}),
          ...(limit ? { limit } : {}),
          ...filters,
        })
        if (!isLatest()) return
        // NCP-FR-007: The API ignores some filters, so they're applied here as well
        const fresh = response.filter((event) => matchesEventFilters(event, filters))
        const merged = since
          ? mergeNewEvents(fresh, previous, range.startTime)
          : sortNewestFirst(fresh)
        const sliced = limit ? merged.slice(0, limit) : merged
        // AES-FR-003: A refresh that brings nothing new keeps the same array, so consumers don't
        // rebuild their rows or mark events read again
        const next = previous && sameEvents(sliced, previous) ? previous : sliced
        loaded.current = { timeframe, filters, events: next }
        setEvents(next)
        setLastUpdated(new Date())
      } catch {
        if (!isLatest()) return
        setError(true)
        // NCP-FR-008: A failed load doesn't present old rows as current; a failed background
        // refresh keeps them (with the error shown) so the table and its page stay put
        if (!inBackground) {
          loaded.current = null
          setEvents([])
        }
      } finally {
        if (pending.current?.id === requestId) pending.current = null
        if (isLatest()) setLoading(false)
      }
    },
    [timeframe, filters, limit, isMounted],
  )

  const refetch = useCallback(() => fetchEvents(false), [fetchEvents])
  const refreshInBackground = useCallback(() => fetchEvents(true), [fetchEvents])

  useEffect(() => {
    refetch()
  }, [refetch])

  return { events, loading, error, lastUpdated, refetch, refreshInBackground }
}
